<template>
    <MainContent title="Crear deportista">
        <Card customClasses="tw:rounded-lg tw:p-8 tw:md:p-12">
            <Stepper
                :steps="steps"
                :current-step="currentStep"
                @update:current-step="currentStep = $event"
            />

            <div class="tw:mt-8">
                <h2 class="tw:text-2xl tw:font-bold">
                    {{ stepTitle }}
                </h2>
                <p class="tw:text-secondary tw:text-sm tw:m-0">
                    {{ stepDescription }}
                </p>
            </div>

            <div class="tw:bg-gray-100 tw:w-full tw:h-px tw:my-6"></div>

            <StepperPasoUno
                v-if="currentStepName === 'Jugador'"
                ref="stepRef"
                :categorias="categorias"
                :equipos="equipos"
                :loading="loading"
                :submitting="submitting"
                :phone-valid="phoneValid"
                :fields="fieldsDeportistaFiltered"
                @phone-valid-change="onPhoneValidUpdate"
                @categoria-change="onCategoriaChange"
            />

            <StepperPasoDos
                v-if="currentStepName === 'Responsables'"
                ref="step2Ref"
                :submitting="submitting"
                :fields="fieldsResponsableFiltered"
            />

            <StepperPasoTres
                v-if="currentStepName === 'Documentación'"
                ref="step3Ref"
            />

            <StepperPasoCuatro
                v-if="currentStepName === 'Indumentarias'"
                ref="step4Ref"
                :indumentarias="indumentarias"
                :loading="indumentariasLoading"
            />

            <StepperPasoDescuentos
                v-if="currentStepName === 'Descuentos'"
                ref="step5Ref"
                :importe-total="importeTotal"
                :descuento-options="descuentoOptions"
            />

            <StepperPasoConfirmacion
                v-if="currentStepName === 'Confirmación'"
                ref="step6Ref"
                :importe-total="importeTotal"
                :metodos-pago="metodosPago"
                :categoria-nombre="categoriaNombre"
                :precio-base="precioBase"
                :categoria="categoriaSeleccionada"
                :plazos-data="plazosData"
                :errores="erroresFinal"
            />

            <div class="tw:mt-8 tw:flex tw:flex-row tw:justify-end tw:gap-2 tw:sm:gap-4" v-if="!finalizado">
                <Button
                    v-if="currentStep > 1"
                    variant="ghost"
                    customClass="tw:w-1/2 tw:sm:w-auto tw:justify-center tw:border tw:border-gray-100"
                    @click="prevStep"
                >
                    Volver
                </Button>
                <Button
                    :loading="submitting"
                    :disabled="!canFinalize"
                    customClass="tw:w-1/2 tw:sm:w-auto"
                    @click="handleContinue"
                >
                    {{ currentStepName === 'Confirmación' ? 'FINALIZAR' : 'CONTINUAR' }}
                </Button>
            </div>

            <!-- Éxito -->
            <div v-if="finalizado" class="tw:mt-8 tw:p-6 tw:bg-green-50 tw:border tw:border-green-200 tw:rounded-xl tw:text-center tw:flex tw:justify-center tw:items-center tw:flex-col">
                <svg class="tw:mx-auto tw:w-12 tw:h-12 tw:text-green-500 tw:mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <h3 class="tw:text-lg tw:font-bold tw:text-green-800 tw:mb-1">¡Registro finalizado correctamente!</h3>
                <p class="tw:text-sm tw:text-green-700 tw:mb-4">El deportista ha sido creado con éxito.</p>
                <Button customClass="tw:justify-center" @click="$router.push({ name: 'back.bases.index', params: { lista_id: listaId } })">
                    Volver al listado de bases
                </Button>
            </div>
        </Card>
    </MainContent>
</template>

<script setup>
    import { ref, provide, reactive, computed, getCurrentInstance, watch } from 'vue';
    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import Stepper from '@/modules/admin/ui/Stepper.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import StepperPasoUno from '@/modules/admin/bases/crear/components/StepperPasoUno.vue';
    import StepperPasoDos from '@/modules/admin/bases/crear/components/StepperPasoDos.vue';
    import StepperPasoTres from '@/modules/admin/bases/crear/components/StepperPasoTres.vue';
    import StepperPasoCuatro from '@/modules/admin/bases/crear/components/StepperPasoCuatro.vue';
    import StepperPasoDescuentos from '@/modules/admin/bases/crear/components/StepperPasoDescuentos.vue';
    import StepperPasoConfirmacion from '@/modules/admin/bases/crear/components/StepperPasoConfirmacion.vue';
    import { useCrearDeportista } from '@/composables/admin/bases/useCrearDeportista';

    const instance = getCurrentInstance();
    const listaId = Number(instance.proxy.$route.params.lista_id);

    const stepRef = ref(null);
    const step2Ref = ref(null);
    const step3Ref = ref(null);
    const step4Ref = ref(null);
    const step5Ref = ref(null);
    const step6Ref = ref(null);

    // ── Validez reactiva del paso 6 (Confirmación) para deshabilitar FINALIZAR ──
    //    StepperPasoConfirmacion actualiza isStepValid directamente sobre
    //    este objeto compartido via provide/inject (más fiable que event chain)
    const step6State = reactive({ isStepValid: true });
    provide('step6State', step6State);
    const canFinalize = computed(() => {
        if (currentStepName.value !== 'Confirmación') return true;
        return step6State.isStepValid;
    });

    const {
        formData,
        currentStep,
        steps,
        loading,
        submitting,
        phoneValid,
        categorias,
        equipos,
        indumentarias,
        indumentariasLoading,
        fieldsDeportistas,
        guardarDatos,
        guardarResponsables,
        guardarDocumentos,
        fetchIndumentarias,
        guardarIndumentarias,
        guardarDescuentos,
        crearBase,
        importeTotal,
        descuentoOptions,
        metodosPago,
        finalizado,
        erroresFinal,
        advanceStep,
        prevStep,
        onCategoriaChange,
        plazosData,
        plazosLoading,
        plazosErrorMessage,
        calcularPlazos,
        borrarPlazos,
    } = useCrearDeportista(listaId);

    // ── Custom fields filtrados por pivot (mismo patrón que el frontend público) ──
    const fieldsDeportistaFiltered = computed(() =>
        (fieldsDeportistas.value || []).filter(f => f.pivot?.deportista === true)
    );
    const fieldsResponsableFiltered = computed(() =>
        (fieldsDeportistas.value || []).filter(f => f.pivot?.representante === true)
    );

    provide('formData', formData);
    provide('plazosActions', {
        calcularPlazos,
        borrarPlazos,
        plazosData,
        plazosLoading,
        plazosErrorMessage,
    });

    // ── Datos de categoría para el paso de confirmación ──
    const categoriaSeleccionada = computed(() => {
        return categorias.value.find(c => c.id === formData.categoria_base_id) || null;
    });
    const categoriaNombre = computed(() => categoriaSeleccionada.value?.name || categoriaSeleccionada.value?.nombre || '');
    const precioBase = computed(() => categoriaSeleccionada.value?.precio || 0);

    // ── Nombre del paso actual (desde el array de steps) ──
    const currentStepName = computed(() => {
        return steps.value[currentStep.value - 1] || '';
    });

    // ── Título y descripción dinámicos por paso ──
    const stepTitle = computed(() => {
        switch (currentStepName.value) {
            case 'Jugador': return 'Información Básica';
            case 'Responsables': return 'Datos de los responsables';
            case 'Documentación': return 'Documentación del deportista';
            case 'Indumentarias': return 'Indumentaria';
            case 'Descuentos': return 'Descuentos';
            case 'Confirmación': return 'Confirmación';
            default: return currentStepName.value;
        }
    });

    const stepDescription = computed(() => {
        switch (currentStepName.value) {
            case 'Jugador': return 'Complete los campos obligatorios para continuar con el registro del jugador.';
            case 'Responsables': return 'Datos de los padres, madres o tutores legales del deportista.';
            case 'Documentación': return 'Adjunta la documentación necesaria para completar el registro del deportista.';
            case 'Indumentarias': return 'Selecciona las indumentarias para esta categoría.';
            case 'Descuentos': return 'Aplica un descuento sobre el importe total si lo deseas.';
            case 'Confirmación': return 'Revisa el resumen y finaliza el registro del deportista.';
            default: return '';
        }
    });

    // ── Cargar indumentarias cuando se llega a ese paso ──
    watch(currentStepName, name => {
        if (name === 'Indumentarias' && formData.categoria_base_id) {
            fetchIndumentarias(formData.categoria_base_id);
        }
    });

    function onPhoneValidUpdate(val) {
        phoneValid.value = val;
    }

    async function handleContinue() {
        try {
            switch (currentStepName.value) {
                case 'Jugador': {
                    const isValid = stepRef.value
                        ? await stepRef.value.validate()
                        : true;
                    if (!isValid) return;

                    await guardarDatos();
                    break;
                }
                case 'Responsables': {
                    // ── Paso 2: Responsables ──
                    const isValid = step2Ref.value
                        ? await step2Ref.value.validate()
                        : true;

                    if (!isValid) return;

                    await guardarResponsables();
                    break;
                }
                case 'Documentación': {
                    // ── Paso 3: Documentación ──
                    let docData = { documentos_id: [], foto_url: '' };
                    if (step3Ref.value && step3Ref.value.getDocumentData) {
                        docData = step3Ref.value.getDocumentData();
                    }
                    await guardarDocumentos(docData.documentos_id, docData.foto_url);
                    break;
                }
                case 'Indumentarias': {
                    const isValid = step4Ref.value
                        ? await step4Ref.value.validate()
                        : true;

                    if (!isValid) return;

                    const data = step4Ref.value && step4Ref.value.getData
                        ? step4Ref.value.getData()
                        : { selected: [], campoNombre: '', campoNumero: '' };

                    await guardarIndumentarias(data.selected, data.campoNombre, data.campoNumero);
                    break;
                }
                case 'Descuentos': {
                    const data = step5Ref.value && step5Ref.value.getData
                        ? step5Ref.value.getData()
                        : { descuento_aplicado: 0, descuento_integro: 0 };

                    await guardarDescuentos(data.descuento_aplicado, data.descuento_integro);
                    break;
                }
                case 'Confirmación': {
                    const isValid = step6Ref.value
                        ? await step6Ref.value.validate()
                        : true;
                    if (!isValid) return;

                    const data = step6Ref.value.getData
                        ? step6Ref.value.getData()
                        : { metodo_pago: null };

                    await crearBase(data.metodo_pago);
                    break;
                }
                default:
                    advanceStep();
                    break;
            }
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[handleContinue] Error inesperado:', err);
            }
        }
    }
</script>
